import type { TFunction } from 'i18next';

import type {
  AutomationAction,
  AutomationCondition,
  AutomationConditionOperator,
  AutomationEntityType,
  AutomationTriggerEvent,
  PipelineTargetingAutomationCardFragment,
} from '@/gql/graphql';
import { checkUnreachable } from '@payaca/utilities/guards';

// Fragment-specific types that use aliased field names
type FragmentAction =
  PipelineTargetingAutomationCardFragment['actions'][number];
type FragmentCondition =
  PipelineTargetingAutomationCardFragment['conditions'][number];

// Union of schema and fragment types, with __typename made required for narrowing
export type AnyAutomationAction = AutomationAction | FragmentAction;
export type AnyAutomationCondition =
  | (AutomationCondition & {
      __typename: NonNullable<AutomationCondition['__typename']>;
    })
  | (FragmentCondition & {
      __typename: NonNullable<FragmentCondition['__typename']>;
    });

/**
 * Generates a human-readable description of what triggers an automation,
 * based on its entity type and trigger event.
 * Uses templates with entity placeholder for natural sentences.
 */
export function getTriggerDescriptionForAutomation(
  t: TFunction,
  entityType: AutomationEntityType,
  triggerEvent: AutomationTriggerEvent
): string {
  const entityLabel = t(`automations.entityTypes.${entityType}`);
  return t(`automations.triggerEvents.${triggerEvent}`, {
    entity: entityLabel,
  });
}

/**
 * Generates a human-readable description for an automation condition.
 * Supports both schema types and fragment types.
 */
export function getConditionDescription(
  t: TFunction,
  condition: AnyAutomationCondition
): string {
  if (!condition.__typename) {
    return 'Unknown condition';
  }

  if (condition.__typename === 'MisconfiguredAutomationCondition') {
    return 'Misconfigured condition';
  }

  const typename = condition.__typename;

  const operatorLabel = t(
    `automations.conditions.operators.${condition.operator}`,
    { defaultValue: condition.operator.toLowerCase() }
  );

  switch (typename) {
    case 'ConnectionApplicationStatusCondition':
      return t(`automations.conditions.labels.${typename}`, {
        value: t(
          `automations.conditions.statuses.${condition.connectionApplicationStatus}`
        ),
      });

    case 'EventTaskStatusCondition':
      return t(`automations.conditions.labels.${typename}`, {
        value: t(
          `automations.conditions.statuses.${condition.eventTaskStatus}`
        ),
      });

    case 'InvoiceIsDepositCondition': {
      const key = condition.isDepositInvoice
        ? 'InvoiceIsDepositCondition_true'
        : 'InvoiceIsDepositCondition_false';
      return t(`automations.conditions.labels.${key}`);
    }

    case 'InvoiceIsGrantCondition': {
      const key = condition.isGrantInvoice
        ? 'InvoiceIsGrantCondition_true'
        : 'InvoiceIsGrantCondition_false';
      return t(`automations.conditions.labels.${key}`);
    }

    case 'InvoiceStatusCondition':
      return t(`automations.conditions.labels.${typename}`, {
        value: t(`automations.conditions.statuses.${condition.invoiceStatus}`),
      });

    case 'ProposalStatusCondition':
      return t(`automations.conditions.labels.${typename}`, {
        value: t(`automations.conditions.statuses.${condition.proposalStatus}`),
      });

    case 'TriggeredDayOfWeekCondition':
      return t(`automations.conditions.labels.${typename}`, {
        value: t(
          `automations.conditions.dayOfWeek.${condition.triggeredDayOfWeek}`
        ),
      });

    case 'ProjectPipelineCondition':
      return t(`automations.conditions.labels.${typename}`, {
        value: condition.pipeline?.title ?? 'Unknown',
      });

    // Tag-based conditions with static values
    case 'EventDoesNotHaveTagCondition':
    case 'EventHasTagCondition':
    case 'ProjectDoesNotHaveTagCondition':
    case 'ProjectHasTagCondition':
    case 'NewlyAddedTagCondition':
      return t(`automations.conditions.labels.${typename}`, {
        value: condition.value,
      });

    // String match conditions with operator
    case 'AssigneesPhoneCondition':
    case 'CustomerEmailCondition':
    case 'CustomerNameCondition':
    case 'CustomerPhoneCondition':
    case 'EventAssigneesEmailCondition':
    case 'LocationContactsEmailCondition':
    case 'LocationContactsPhoneCondition':
    case 'NewPipelineStageNameCondition':
    case 'OldPipelineStageNameCondition':
    case 'ProjectPipelineStageNameCondition':
    case 'ProjectSiteAddressContactsEmailCondition':
    case 'ProjectSiteAddressContactsPhoneCondition':
    case 'UserEmailCondition':
      return formatStringMatchCondition(t, typename, operatorLabel, condition);

    default: {
      // Exhaustive check - this should never be reached
      checkUnreachable(typename);
      return `Unknown condition: ${typename as string}`;
    }
  }
}

/**
 * Generates a human-readable description for an automation action.
 * Supports both schema types (with `title`, `targetStage`) and
 * fragment types (with aliased fields like `taskTitle`, `progressionTargetStage`).
 */
export function getActionDescription(
  t: TFunction,
  action: AnyAutomationAction
): string {
  const typename = action.__typename;
  if (!typename) {
    return 'Unknown action';
  }

  if (typename === 'UnsupportedAutomationAction') {
    return 'Unsupported action';
  }

  switch (typename) {
    case 'CreateProjectTaskAutomationAction': {
      // Handle both schema field `title` and fragment alias `taskTitle`
      const actionWithFields = action as { title?: string; taskTitle?: string };
      const taskName =
        actionWithFields.taskTitle ?? actionWithFields.title ?? 'Task';
      return t(`automations.actions.labels.${typename}`, { taskName });
    }

    case 'EmailNotificationAutomationAction': {
      const recipient = t(
        `automations.actions.sendToEmail.${action.sendToEmail}`
      );
      return t(`automations.actions.labels.${typename}`, { recipient });
    }

    case 'MoveProjectToPipelineStageAutomationAction':
      return t(`automations.actions.labels.${typename}`, {
        stageName: action.targetStage.title,
      });

    case 'RemoveEventTagsAutomationAction':
    case 'RemoveProjectTagsAutomationAction': {
      const actionWithTags = action as { tags?: Array<{ name: string }> };
      const tagName = actionWithTags.tags?.[0]?.name;
      if (tagName) {
        return t(`automations.actions.labels.${typename}`, { tagName });
      }
      return t(`automations.actions.labels.${typename}_noTag`);
    }

    case 'SMSNotificationAutomationAction': {
      const recipient = t(
        `automations.actions.sendToNumber.${action.sendToNumber}`
      );
      return t('automations.actions.labels.SmsNotificationAutomationAction', {
        recipient,
      });
    }

    case 'TagEventAutomationAction':
    case 'TagProjectAutomationAction': {
      const tagName = action.tags[0]?.name ?? 'Unknown';
      return t(`automations.actions.labels.${typename}`, { tagName });
    }

    case 'CreateRepeatProjectAutomationAction':
      return t(`automations.actions.labels.${typename}`);

    default: {
      // Exhaustive check - this should never be reached
      checkUnreachable(typename);
      return `Unknown action: ${typename as string}`;
    }
  }
}

/**
 * Formats a string match condition with its operator and value.
 */
function formatStringMatchCondition(
  t: TFunction,
  typename: string,
  operatorLabel: string,
  condition: { value: string; operator: AutomationConditionOperator }
): string {
  const fieldLabel = t(`automations.conditions.labels.${typename}`);
  return `${fieldLabel} ${operatorLabel} '${condition.value}'`;
}
